import type { FC, PropsWithChildren } from 'react'
import type { ChannelListMessengerProps, DefaultStreamChatGenerics } from 'stream-chat-react'
import { useChannelListContext } from 'stream-chat-react'

import { ErrorCard } from 'src/features/chats/components/shared/error-card'
import { useChatClient } from 'src/features/chats/context/chat-context'
import { LockGraphicIcon } from 'src/shared/components/icons/lock-graphic'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'

interface SidebarChannelListProps extends ChannelListMessengerProps<DefaultStreamChatGenerics> {
  emptyStateVariant?: 'default' | 'navbar'
}

export const SidebarChannelList: FC<PropsWithChildren<SidebarChannelListProps>> = ({ children, loading, error }) => {
  const { channels: loadedChannels } = useChannelListContext()
  const { client, isConnecting, reconnect } = useChatClient()

  const isLoading = loading || isConnecting || !client

  const handleRetry = async () => {
    try {
      await reconnect()
      window.location.reload()
    } catch (error) {
      console.error('Reconnection failed:', error)
    }
  }

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return (
      <ErrorCard description="You got disconnected from chat" buttonText="Refresh Messages" onRetry={handleRetry} />
    )
  }

  if (loadedChannels.length === 0) {
    return <EmptyState />
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-background">
      <div className="flex-1 divide-y divide-border overflow-y-auto">{children}</div>
    </div>
  )
}

export const EmptyState = () => {
  return (
    <div className="flex h-[325px] flex-col items-center justify-center rounded-lg border bg-background p-4 px-10 py-16">
      <LockGraphicIcon className="mb-4 size-[75px] text-[#495954]" />
      <p className="text-center text-sm text-[#495954]">Unlock your match of the week or send your first referral</p>
    </div>
  )
}

const Loading = () => {
  return (
    <div className="divide-y divide-border">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-start space-x-3 p-3">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-12" />
            </div>
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}
